import Spinner, { SpinnerType } from 'legacy/common/components/spinner/Spinner';
import { useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { useCheckout } from 'features/events/checkout/context';
import { useViewEvent } from 'features/events/context/ViewEvent';
import { getVerifyCode } from 'legacy/events/hooks';
import { useTranslation } from 'react-i18next';

const SpecialCode = () => {
  const { t } = useTranslation(['checkout']);
  const [formErrorMessage, setFormErrorMessage] = useState(null);
  const { event } = useViewEvent();
  const {
    specialCode,
    ticketTiers,
    isLoading,
    applyDiscountCodeAndSave,
    appliedDiscount,
    toggleTicketTierAndSave,
    tickets
  } = useCheckout();
  const [codeValue, setCodeValue] = useState('');

  const resetCode = () => {
    specialCode.code.setCode(null);
    setCodeValue('');
  };

  /**
   * Callback function called when special private code (discount code or private tier unlock code) is successfully verified.
   */
  const onVerifyCodeSuccess = (data) => {
    if (data.type === 'discountCode' || data.type === 'promoterAttribution') {
      if (Object.keys(ticketTiers.privateTierCodes).length > 0) {
        setFormErrorMessage(
          t('checkout:cannotApplyDiscountCodesToPrivateTiers')
        );
        return;
      }

      applyDiscountCodeAndSave(specialCode.code.value, data.type);
      resetCode();
    } else if (data.type === 'privateTier') {
      if (appliedDiscount?.data?.code) {
        setFormErrorMessage(
          t('checkout:cannotApplyDiscountCodesToPrivateTiers')
        );
        return;
      }

      if (ticketTiers.isCodeAlreadyActive(specialCode.code.value)) {
        setFormErrorMessage(t('checkout:privateCodeAlreadyUnlocked'));
        return;
      }

      ticketTiers.unlockPrivateTier(specialCode.code.value, {
        onSuccess: (unlockedTier) => {
          if (!ticketTiers.isTierDisabled(unlockedTier.tierId)) {
            if (ticketTiers.isTierRequiringApproval(unlockedTier.tierId)) {
              tickets.toggleTierRequiringApproval(unlockedTier.tierId);
            } else {
              toggleTicketTierAndSave(
                unlockedTier.tierId,
                specialCode.code.value
              );
            }
          }
          resetCode();
        }
      });
    }
  };

  /**
   * Verifies the provided special code whenever it updates.
   */
  const verifiedCodeData = getVerifyCode(event._id, specialCode.code.value, 1, {
    onSuccess: onVerifyCodeSuccess
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrorMessage(null);
    specialCode.code.setCode(codeValue);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCodeValue(e.target.value.trim());
  };

  const errorMessage =
    ticketTiers.getTierFromShareTokenError?.message ||
    verifiedCodeData.error?.message ||
    formErrorMessage;

  return (
    <div className="flex flex-col md:w-1/3 md:min-w-[350px] px-4">
      <form onSubmit={onSubmit}>
        <label
          htmlFor="specialCodeInput"
          className="text-neutral-900 text-sm font-normal"
        >
          {t('checkout:haveASpecialCode')}
          <div className="relative flex items-center pt-1">
            <input
              id="specialCodeInput"
              type="text"
              placeholder={t('checkout:enterDiscountPrivatePromoterCode')}
              className="w-full border border-neutral-200 rounded-lg p-4 text-neutral-900 text-base font-normal placeholder:text-neutral-500 focus:border-neutral-900 bg-white"
              value={codeValue}
              onChange={onChange}
            />
            {codeValue && (
              <button
                type="submit"
                disabled={isLoading}
                aria-label={t('checkout:applyCode')}
                className="absolute right-[4px] text-black text-xl px-4 py-4 hover:text-black/60 bg-white"
              >
                {isLoading ? (
                  <Spinner type={SpinnerType.DEFAULT} />
                ) : (
                  <FaCheck />
                )}
              </button>
            )}
          </div>
        </label>
        {errorMessage && (
          <div className="text-error pt-2 font-normal text-sm">
            {errorMessage}
          </div>
        )}
      </form>
    </div>
  );
};

export default SpecialCode;
